import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { gettingExceptionRule } from 'utils/ExceptionRule'
import { DataGridPro } from "@mui/x-data-grid-pro";
import type {
    GridColDef,
    GridLocaleText,
    GridRenderCellParams,
    GridRowId,
    GridRowSelectionModel,
} from "@mui/x-data-grid-pro";
import { ListItemIcon, ListItemText, Menu, MenuItem, Chip, Tooltip, Grid } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import CachedIcon from '@mui/icons-material/Cached';
import CheckIcon from '@mui/icons-material/Check';
import { AddExceptionRule } from "components/dialog/exception_rules/AddExceptionRule";
import { DeleteExceptionRule } from "components/dialog/exception_rules/DeleteExceptionRule";
import { InfoExceptionRule } from "components/dialog/exception_rules/InfoExceptionRule";
import { EditExceptionRule } from "components/dialog/exception_rules/EditExceptionRule";
import { buildSettingsBasePath, buildSettingsCrudPath, parseSettingsCrudRoute } from 'utils/settingsCrudRouting';

type ContextMenuPosition = {
    mouseX: number;
    mouseY: number;
};

type ExceptionRuleRow = {
    id: GridRowId;
    name: string;
    no_planning: boolean;
    long_interval: string;
    shortened_shift: string;
    punch_in?: Array<string | null | undefined>;
    punch_out?: Array<string | null | undefined>;
    description?: string;
    in_paycode_id?: number | string | null;
    out_paycode_id?: number | string | null;
    in_paycode?: unknown;
    out_paycode?: unknown;
};

type ContextMenuProps = {
    contextMenu: ContextMenuPosition | null;
    setContextMenu: React.Dispatch<React.SetStateAction<ContextMenuPosition | null>>;
    selectedRow: GridRowSelectionModel;
    setSelectedRow: React.Dispatch<React.SetStateAction<GridRowSelectionModel>>;
    reloading: () => void;
    rows: ExceptionRuleRow[];
};

type ContextMenuOption = {
    name: string;
    icon: React.ReactNode;
    click: () => void;
    color: 'error' | '';
    disabled?: boolean;
    permission: boolean;
};

const getPermissions = (): string[] => {
    try {
        return JSON.parse(localStorage.getItem('permissions') || '[]') as string[];
    } catch {
        return [];
    }
};

const ContextMenu = ({ contextMenu, setContextMenu, selectedRow, setSelectedRow, reloading, rows }: ContextMenuProps) => {
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()

    const [openDelete, setOpenDelete] = useState(false)
    const basePath = buildSettingsBasePath('exceptions')
    const modalRoute = useMemo(() => parseSettingsCrudRoute(location.pathname, 'exceptions'), [location.pathname])
    const openAdd = modalRoute.action === 'add'
    const openEdit = modalRoute.action === 'update' && modalRoute.id !== null
    const openInfo = modalRoute.action === 'detail' && modalRoute.id !== null
    const selectedRowsForRoute = modalRoute.id !== null ? [modalRoute.id] : selectedRow

    const closeRouteModal = () => {
        navigate(basePath)
    }

    const permissions = useMemo(() => getPermissions(), [])

    const options: ContextMenuOption[] = [
        {
            name: t('settings.exception_rules.table.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                navigate(buildSettingsCrudPath('exceptions', 'add'))
                setContextMenu(null)
            },
            color: '',
            permission: permissions.includes('timekeeper.CreateExceptionRule')
        },
        {
            name: t('settings.exception_rules.table.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                const current = selectedRow[0]
                if (!current) return
                navigate(buildSettingsCrudPath('exceptions', 'update', Number(current)))
                setContextMenu(null)
            },
            color: '',
            disabled: selectedRow.length !== 1,
            permission: selectedRow.length === 1 && permissions.includes('timekeeper.PatchExceptionRule')
        },
        {
            name: t('settings.exception_rules.table.context_menu.delete'),
            icon: <DeleteIcon color='error' />,
            click: () => {
                setOpenDelete(true)
                setContextMenu(null)
            },
            color: 'error',
            disabled: true, //selectedRow.length <= 0 ? true : false,
            permission: selectedRow.length === 1 && permissions.includes('timekeeper.DeleteExceptionRule')
        },
        {
            name: t('settings.exception_rules.table.context_menu.info'),
            icon: <InfoIcon />,
            disabled: selectedRow.length !== 1,
            click: () => {
                const current = selectedRow[0]
                if (!current) return
                navigate(buildSettingsCrudPath('exceptions', 'detail', Number(current)))
                setContextMenu(null)
            },
            color: '',
            permission: selectedRow.length === 1 && permissions.includes('timekeeper.ExceptionRulesById')
        },
        {
            name: t('settings.exception_rules.table.context_menu.reload'),
            icon: <CachedIcon />,
            color: '',
            click: () => {
                reloading()
                setContextMenu(null)
            },
            permission: true
        }
    ]


    return (
        <>
            <Menu
                open={Boolean(contextMenu)}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                sx={{ boxShadow: 'none' }}
            >
                {options.filter(option => option.permission).map((option, index) =>
                    <MenuItem key={index} onClick={option.click} disabled={option.disabled}>
                        <ListItemIcon>
                            {option.icon}
                        </ListItemIcon>

                        <ListItemText primary={option.name} slotProps={{ primary: { color: option.color } }} />
                    </MenuItem>
                )}
            </Menu>

            <AddExceptionRule open={openAdd} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal() }} reloading={reloading} rows={rows} />
            <EditExceptionRule open={openEdit} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal() }} selectedRow={selectedRowsForRoute} reloading={reloading} rows={rows} setSelectedRow={setSelectedRow} />
            <DeleteExceptionRule open={openDelete} setOpenDelete={setOpenDelete} selectedRow={selectedRow} reloading={reloading} rows={rows} setSelectedRow={setSelectedRow} />
            <InfoExceptionRule open={openInfo} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal() }} selectedRow={selectedRowsForRoute} rows={rows} setSelectedRow={setSelectedRow} />
        </>
    )
}

const ExcpetionRuleTable = () => {
    const { t } = useTranslation()
    const [loadingExceptionRule, setLoadingExceptionRule] = useState(true)
    const [rows, setRows] = useState<ExceptionRuleRow[]>([])
    const [contextMenu, setContextMenu] = useState<ContextMenuPosition | null>(null)
    const [selectedRow, setSelectedRow] = useState<GridRowSelectionModel>([])

    const permissions = useMemo(() => getPermissions(), [])

    const columns: GridColDef<ExceptionRuleRow>[] = [
        { field: 'name', headerName: t('settings.exception_rules.table.columns.name'), flex: 1, headerAlign: 'center', align: 'left' },
        {
            field: 'no_planning',
            headerName: t('settings.exception_rules.table.columns.no_planning'),
            flex: 0.5,
            headerAlign: 'center',
            align: 'center',
            filterable: false,
            renderCell: (params: GridRenderCellParams<ExceptionRuleRow, boolean>) => {
                return (
                    params.value === true ? <CheckIcon style={{ marginTop: '10px', marginBottom: '10px' }} /> : ''
                )
            }
        },
        {
            field: 'long_interval',
            headerName: t('settings.exception_rules.table.columns.long_interval'),
            flex: 0.6,
            headerAlign: 'center',
            align: 'center'
        },
        {
            field: 'shortened_shift',
            headerName: t('settings.exception_rules.table.columns.shortened_shift'),
            flex: 0.6,
            headerAlign: 'center',
            align: 'center'
        },
        {
            field: 'punch_in',
            headerName: t('settings.exception_rules.table.columns.punch_in'),
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            filterable: false,
            renderCell: (params: GridRenderCellParams<ExceptionRuleRow, ExceptionRuleRow['punch_in']>) => {
                const values = Array.isArray(params.value) ? params.value : []
                return (
                    <Grid container spacing={1.5}>
                        <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
                            <Tooltip title={t('settings.exception_rules.table.columns.in_punch_very_early')} arrow>
                                {
                                    values[0] ? <Chip label={values[0]} /> : <span>--</span>
                                }
                            </Tooltip>
                        </Grid>
                        <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
                            <Tooltip title={t('settings.exception_rules.table.columns.in_punch_early')} arrow>
                                {
                                    values[1] ? <Chip label={values[1]} /> : <span>--</span>
                                }
                            </Tooltip>
                        </Grid>
                        <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
                            <Tooltip title={t('settings.exception_rules.table.columns.in_punch_late')} arrow>
                                {
                                    values[2] ? <Chip label={values[2]} /> : <span>--</span>
                                }
                            </Tooltip>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            field: 'punch_out',
            headerName: t('settings.exception_rules.table.columns.punch_out'),
            flex: 1,
            headerAlign: 'center',
            align: 'left',
            filterable: false,
            renderCell: (params: GridRenderCellParams<ExceptionRuleRow, ExceptionRuleRow['punch_out']>) => {
                const values = Array.isArray(params.value) ? params.value : []
                return (
                    <Grid container spacing={1.5}>
                        <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
                            <Tooltip title={t('settings.exception_rules.table.columns.out_punch_very_late')} arrow>
                                {
                                    values[0] ? <Chip label={values[0]} /> : <span>--</span>
                                }
                            </Tooltip>
                        </Grid>
                        <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
                            <Tooltip title={t('settings.exception_rules.table.columns.out_punch_early')} arrow>
                                {
                                    values[1] ? <Chip label={values[1]} /> : <span>--</span>
                                }
                            </Tooltip>
                        </Grid>
                        <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
                            <Tooltip title={t('settings.exception_rules.table.columns.out_punch_late')} arrow>
                                {
                                    values[2] ? <Chip label={values[2]} /> : <span>--</span>
                                }
                            </Tooltip>
                        </Grid>
                    </Grid>
                )
            }
        },
    ];

    const localeText: Partial<GridLocaleText> = {
        headerFilterOperatorContains: t('general.table.headerFilters.headerFilterOperatorContains'),
        headerFilterOperatorEquals: t('general.table.headerFilters.headerFilterOperatorEquals'),
        headerFilterOperatorStartsWith: t('general.table.headerFilters.headerFilterOperatorStartsWith'),
        headerFilterOperatorEndsWith: t('general.table.headerFilters.headerFilterOperatorEndsWith'),
        headerFilterOperatorIsEmpty: t('general.table.headerFilters.headerFilterOperatorIsEmpty'),
        headerFilterOperatorIsNotEmpty: t('general.table.headerFilters.headerFilterOperatorIsNotEmpty'),
        footerRowSelected: (count) =>
            count > 1
                ? t('general.table.footerFilters.footerRowSelected.>1', { count: count.toLocaleString() })
                : t('general.table.footerFilters.footerRowSelected.=1', { count: count.toLocaleString() }),
        footerTotalRows: t('general.table.footerFilters.footerTotalRows'),
        MuiTablePagination: {
            labelDisplayedRows: ({ from, to, count }) =>
                t('general.table.footerFilters.labelDisplayedRows', { from: from.toLocaleString(), to: to.toLocaleString(), count: count.toLocaleString() }),
            labelRowsPerPage: t('general.table.footerFilters.labelRowsPerPage'),
        },
        columnsManagementSearchTitle: t('general.table.headerFilters.columnsManagementSearchTitle'),
        columnsManagementNoColumns: t('general.table.headerFilters.columnsManagementNoColumns'),
        columnsManagementShowHideAllText: t('general.table.headerFilters.columnsManagementShowHideAllText'),
        columnsManagementReset: t('general.table.headerFilters.columnsManagementReset'),
        filterPanelAddFilter: t('general.table.headerFilters.filterPanelAddFilter'),
        filterPanelRemoveAll: t('general.table.headerFilters.filterPanelRemoveAll'),
        filterPanelDeleteIconLabel: t('general.table.headerFilters.filterPanelDeleteIconLabel'),
        filterPanelOperator: t('general.table.headerFilters.filterPanelOperator'),
        filterPanelOperatorAnd: t('general.table.headerFilters.filterPanelOperatorAnd'),
        filterPanelOperatorOr: t('general.table.headerFilters.filterPanelOperatorOr'),
        filterPanelColumns: t('general.table.headerFilters.filterPanelColumns'),
        filterPanelInputLabel: t('general.table.headerFilters.filterPanelInputLabel'),
        filterPanelInputPlaceholder: t('general.table.headerFilters.filterPanelInputPlaceholder'),
        filterOperatorContains: t('general.table.headerFilters.filterOperatorContains'),
        filterOperatorEquals: t('general.table.headerFilters.filterOperatorEquals'),
        filterOperatorStartsWith: t('general.table.headerFilters.filterOperatorStartsWith'),
        filterOperatorEndsWith: t('general.table.headerFilters.filterOperatorEndsWith'),
        filterOperatorIs: t('general.table.headerFilters.filterOperatorIs'),
        filterOperatorNot: t('general.table.headerFilters.filterOperatorNot'),
        filterOperatorAfter: t('general.table.headerFilters.filterOperatorAfter'),
        filterOperatorOnOrAfter: t('general.table.headerFilters.filterOperatorOnOrAfter'),
        filterOperatorBefore: t('general.table.headerFilters.filterOperatorBefore'),
        filterOperatorOnOrBefore: t('general.table.headerFilters.filterOperatorOnOrBefore'),
        filterOperatorIsEmpty: t('general.table.headerFilters.filterOperatorIsEmpty'),
        filterOperatorIsNotEmpty: t('general.table.headerFilters.filterOperatorIsNotEmpty'),
        filterOperatorIsAnyOf: t('general.table.headerFilters.filterOperatorIsAnyOf'),
        filterOperator_equals: t('general.table.headerFilters.filterOperator_equals'),
        columnMenuLabel: t('general.table.headerFilters.columnMenuLabel'),
        columnMenuShowColumns: t('general.table.headerFilters.columnMenuShowColumns'),
        columnMenuManageColumns: t('general.table.headerFilters.columnMenuManageColumns'),
        columnMenuFilter: t('general.table.headerFilters.columnMenuFilter'),
        columnMenuHideColumn: t('general.table.headerFilters.columnMenuHideColumn'),
        columnMenuUnsort: t('general.table.headerFilters.columnMenuUnsort'),
        columnMenuSortAsc: t('general.table.headerFilters.columnMenuSortAsc'),
        columnMenuSortDesc: t('general.table.headerFilters.columnMenuSortDesc'),
        pinToLeft: t('general.table.cellFilters.pinToLeft'),
        pinToRight: t('general.table.cellFilters.pinToRight'),
        checkboxSelectionHeaderName: t('general.table.checkboxFilters.checkboxSelectionHeaderName'),
    }

    const reloading = () => {
        if (permissions.includes('timekeeper.ExceptionRules')) {
            gettingExceptionRule(setLoadingExceptionRule, setRows)
        } else {
            setRows([])
            setLoadingExceptionRule(false)
        }
    }

    useEffect(() => {
        reloading()
    }, [])

    return (

        <Grid container
            style={{ width: '100%', height: '100%' }}
            onContextMenu={(event) => {
                event.preventDefault();
                setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null)
            }}
        >
            <DataGridPro
                sx={{ height: 'calc(100vh - 140px)' }}
                columns={columns}
                rows={rows}
                showCellVerticalBorder
                showColumnVerticalBorder
                headerFilters
                localeText={localeText}
                loading={loadingExceptionRule}
                checkboxSelection
                onRowSelectionModelChange={(newRowSelectionModel) => {
                    setSelectedRow(newRowSelectionModel);
                }}
                rowSelectionModel={selectedRow}
                pagination
            />

            <ContextMenu
                contextMenu={contextMenu}
                setContextMenu={setContextMenu}
                selectedRow={selectedRow}
                setSelectedRow={setSelectedRow}
                reloading={reloading}
                rows={rows}
            />

        </Grid>

    )
}

export { ExcpetionRuleTable }